import { useEffect, useState } from "react";
import { RefreshCw, Eye, EyeOff, Instagram, Linkedin, Mail, Phone, XCircle, Filter, X } from "lucide-react";
import ReactApexChart from "react-apexcharts";
import BONavigation from "../../../components/admin/BONavigation";
import {
    getOverviewStats, getKpiHistory, getSignalTypeCounts,
    OverviewStats, KpiSnapshot, OverviewStatsFilters,
} from "../../../interfaces/establishments";

function pct(num: number, den: number): string {
    if (!den) return "—";
    return (num / den * 100).toFixed(1) + " %";
}

// ─── Signal types ─────────────────────────────────────────────────────────────
const SIGNAL_TYPES: { value: string; label: string; color: string }[] = [
    { value: "multi_site",               label: "Multi-site",          color: "#7c3aed" },
    { value: "restaurant_opening",       label: "Ouverture",           color: "#16a34a" },
    { value: "franchise_signing",        label: "Franchise",           color: "#2563eb" },
    { value: "network_expansion",        label: "Expansion réseau",    color: "#0d9488" },
    { value: "restaurant_owner_change",  label: "Changement proprio",  color: "#ea580c" },
    { value: "restaurant_closing",       label: "Fermeture",           color: "#ef4444" },
    { value: "hygiene_control",          label: "Hygiène",             color: "#d97706" },
];

// ─── KpiCard ──────────────────────────────────────────────────────────────────
interface KpiCardProps {
    label: string;
    value: number | null;
    percent?: string;
    icon: React.ReactNode;
    sub?: { label: string; value: number | null; percent?: string }[];
    accent?: "green" | "red" | "blue" | "purple" | "default";
}

function KpiCard({ label, value, percent, icon, sub, accent = "default" }: KpiCardProps) {
    const valueColor: Record<string, string> = {
        green: "text-green-600",
        red: "text-red-500",
        blue: "text-blue-600",
        purple: "text-purple-600",
        default: "text-gray-900",
    };

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-3 flex flex-col gap-2">
            <div className="flex items-center gap-1.5 text-gray-400">
                {icon}
                <span className="text-xs font-medium">{label}</span>
            </div>
            <div>
                <p className={`text-xl font-semibold leading-tight ${valueColor[accent]}`}>
                    {value === null ? "—" : value.toLocaleString("fr-FR")}
                </p>
                {percent && (
                    <p className="text-xs text-gray-400 mt-0.5">{percent}</p>
                )}
            </div>
            {sub && sub.length > 0 && (
                <div className="border-t border-gray-100 pt-2 flex flex-col gap-1">
                    {sub.map((s) => (
                        <div key={s.label} className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">{s.label}</span>
                            <div className="text-right">
                                <span className="text-xs font-semibold text-gray-700">
                                    {s.value === null ? "—" : s.value.toLocaleString("fr-FR")}
                                </span>
                                {s.percent && (
                                    <span className="text-xs text-gray-400 ml-1">· {s.percent}</span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Chart ────────────────────────────────────────────────────────────────────
const CHART_METRICS: {
    key: string;
    label: string;
    color: string;
    getValue: (s: KpiSnapshot) => number;
}[] = [
    { key: "total",         label: "Total",                color: "#1A1A1A", getValue: (s) => s.establishments.total },
    { key: "displayed",     label: "Affichés",             color: "#16a34a", getValue: (s) => s.establishments.displayed },
    { key: "closed",        label: "Fermés",               color: "#ef4444", getValue: (s) => s.establishments.closed ?? 0 },
    { key: "withEmail",     label: "Business email",       color: "#2563eb", getValue: (s) => s.establishments.withEmail },
    { key: "withInstagram", label: "Instagram",            color: "#9333ea", getValue: (s) => s.establishments.withInstagram },
    { key: "withTags",      label: "Avec tags",            color: "#ea580c", getValue: (s) => s.establishments.withTags ?? 0 },
    { key: "withSubTags",   label: "Avec sous-tags",       color: "#0d9488", getValue: (s) => s.establishments.withSubTags ?? 0 },
    { key: "phone",         label: "Téléphones",           color: "#d97706", getValue: (s) => s.establishments.phone.total },
    { key: "linkedin",      label: "LinkedIn approuvés",   color: "#0284c7", getValue: (s) => s.people.linkedinApproved },
    { key: "personalEmail", label: "Email perso approuvé", color: "#059669", getValue: (s) => s.people.personalEmailsApproved },
];

function KpiChart({ snapshots }: { snapshots: KpiSnapshot[] }) {
    const [active, setActive] = useState<Set<string>>(
        new Set(["total", "displayed", "withEmail", "linkedin"])
    );

    const toggle = (key: string) => {
        setActive((prev) => {
            const next = new Set(prev);
            if (next.has(key)) { if (next.size > 1) next.delete(key); }
            else next.add(key);
            return next;
        });
    };

    const categories = snapshots.map((s) =>
        new Date(s.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })
    );

    const series = CHART_METRICS.filter((m) => active.has(m.key)).map((m) => ({
        name: m.label,
        data: snapshots.map((s) => m.getValue(s)),
        color: m.color,
    }));

    const options: ApexCharts.ApexOptions = {
        chart: {
            type: "line",
            toolbar: { show: false },
            zoom: { enabled: false },
            fontFamily: "inherit",
            background: "transparent",
        },
        stroke: { curve: "smooth", width: 2 },
        grid: { borderColor: "#f3f4f6", strokeDashArray: 4 },
        xaxis: {
            categories,
            labels: { style: { colors: "#9ca3af", fontSize: "11px" }, rotate: -30 },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            labels: {
                style: { colors: "#9ca3af", fontSize: "11px" },
                formatter: (v: number) => v.toLocaleString("fr-FR"),
            },
        },
        tooltip: {
            y: { formatter: (v: number) => v.toLocaleString("fr-FR") },
        },
        legend: { show: false },
        markers: { size: snapshots.length <= 14 ? 4 : 0 },
    };

    if (snapshots.length < 2) {
        return (
            <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-sm text-gray-400">
                Les graphiques apparaîtront une fois que plusieurs snapshots auront été collectés (un par jour).
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex flex-wrap gap-2 mb-4">
                {CHART_METRICS.map((m) => (
                    <button
                        key={m.key}
                        onClick={() => toggle(m.key)}
                        className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                            active.has(m.key)
                                ? "border-transparent text-white"
                                : "border-gray-200 text-gray-400 bg-white"
                        }`}
                        style={active.has(m.key) ? { backgroundColor: m.color, borderColor: m.color } : {}}
                    >
                        {m.label}
                    </button>
                ))}
            </div>
            <ReactApexChart
                options={options}
                series={series}
                type="line"
                height={280}
                width="100%"
            />
        </div>
    );
}

// ─── FilterBar ────────────────────────────────────────────────────────────────
interface FilterBarProps {
    filters: OverviewStatsFilters;
    signalCounts: Record<string, number>;
    onChange: (f: OverviewStatsFilters) => void;
}

function FilterBar({ filters, signalCounts, onChange }: FilterBarProps) {
    const hasActiveFilters = !!(filters.signalType || filters.noSignals || filters.isFranchise || filters.category);

    const toggleSignal = (value: string) => {
        // mutually exclusive with noSignals
        const isActive = filters.signalType === value;
        onChange({ ...filters, signalType: isActive ? undefined : value, noSignals: undefined });
    };

    const toggleNoSignals = () => {
        onChange({ ...filters, noSignals: filters.noSignals ? undefined : true, signalType: undefined });
    };

    const clearAll = () => onChange({});

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-3 flex flex-col gap-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-gray-500">
                    <Filter className="w-3.5 h-3.5" />
                    <span className="text-xs font-medium">Filtres</span>
                </div>
                {hasActiveFilters && (
                    <button
                        onClick={clearAll}
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
                    >
                        <X className="w-3 h-3" />
                        Réinitialiser
                    </button>
                )}
            </div>

            {/* Signal type */}
            <div className="flex flex-col gap-1.5">
                <span className="text-xs text-gray-400 font-medium">Signal (établissements)</span>
                <div className="flex flex-wrap gap-1.5">
                    {SIGNAL_TYPES.map((s) => {
                        const isActive = filters.signalType === s.value;
                        const count = signalCounts[s.value];
                        const isEmpty = count === 0;
                        return (
                            <button
                                key={s.value}
                                onClick={() => toggleSignal(s.value)}
                                disabled={isEmpty}
                                title={isEmpty ? "Aucun établissement avec ce signal" : undefined}
                                className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                                    isEmpty
                                        ? "border-gray-100 text-gray-300 bg-white cursor-not-allowed"
                                        : isActive
                                            ? "border-transparent text-white"
                                            : "border-gray-200 text-gray-500 bg-white hover:border-gray-300"
                                }`}
                                style={isActive && !isEmpty ? { backgroundColor: s.color, borderColor: s.color } : {}}
                            >
                                {s.label}
                                {count !== undefined && (
                                    <span className={`font-normal ${isActive ? "opacity-80" : "text-gray-400"}`}>
                                        {count.toLocaleString("fr-FR")}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                    <button
                        onClick={toggleNoSignals}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                            filters.noSignals
                                ? "bg-gray-800 text-white border-gray-800"
                                : "border-gray-200 text-gray-500 bg-white hover:border-gray-300"
                        }`}
                    >
                        Sans signal
                    </button>
                </div>
            </div>

            {/* Extra toggles */}
            <div className="flex flex-wrap items-center gap-2">
                <button
                    onClick={() => onChange({ ...filters, isFranchise: filters.isFranchise ? undefined : true })}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        filters.isFranchise
                            ? "bg-black text-white border-black"
                            : "border-gray-200 text-gray-500 bg-white hover:border-gray-300"
                    }`}
                >
                    Franchise uniquement
                </button>
            </div>
        </div>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function DatabaseStats() {
    const [stats, setStats] = useState<OverviewStats | null>(null);
    const [history, setHistory] = useState<KpiSnapshot[]>([]);
    const [signalCounts, setSignalCounts] = useState<Record<string, number>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFilters] = useState<OverviewStatsFilters>({});

    const hasActiveFilters = !!(filters.signalType || filters.noSignals || filters.isFranchise || filters.category);

    const loadStats = (f: OverviewStatsFilters) => {
        setLoading(true);
        setError(null);
        getOverviewStats(f)
            .then((s) => setStats(s))
            .catch(() => setError("Impossible de charger les statistiques."))
            .finally(() => setLoading(false));
    };

    const loadAll = () => {
        setLoading(true);
        setError(null);
        Promise.all([getOverviewStats(filters), getKpiHistory(), getSignalTypeCounts()])
            .then(([s, h, sc]) => { setStats(s); setHistory(h); setSignalCounts(sc); })
            .catch(() => setError("Impossible de charger les statistiques."))
            .finally(() => setLoading(false));
    };

    useEffect(() => { loadAll(); }, []);

    const handleFilterChange = (f: OverviewStatsFilters) => {
        setFilters(f);
        loadStats(f);
    };

    const e = stats?.establishments;
    const p = stats?.people;
    const total = e?.total ?? 0;
    const phones = e?.phone.total ?? 0;

    // Build active filter label for display
    const filterLabel = (() => {
        const parts: string[] = [];
        if (filters.signalType) {
            const sig = SIGNAL_TYPES.find((s) => s.value === filters.signalType);
            if (sig) parts.push(`Signal : ${sig.label}`);
        }
        if (filters.noSignals) parts.push("Sans signal");
        if (filters.isFranchise) parts.push("Franchise");
        if (filters.category) parts.push(`Catégorie : ${filters.category}`);
        return parts.join(" · ");
    })();

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-6 py-6">
                <BONavigation />

                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl font-semibold text-gray-900">KPIs</h1>
                        {hasActiveFilters && (
                            <span className="text-xs bg-black text-white px-2 py-0.5 rounded-full">
                                {filterLabel}
                            </span>
                        )}
                    </div>
                    <button
                        onClick={loadAll}
                        disabled={loading}
                        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                        Actualiser
                    </button>
                </div>

                {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

                <div className="flex flex-col gap-4">
                    <FilterBar filters={filters} signalCounts={signalCounts} onChange={handleFilterChange} />

                    {loading && !stats && <p className="text-sm text-gray-500">Chargement…</p>}

                    {stats && (
                        <div className="flex flex-col gap-6">
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                                <KpiCard
                                    label="Total"
                                    value={e?.total ?? null}
                                    icon={<Eye className="w-3.5 h-3.5" />}
                                />
                                <KpiCard
                                    label="Affichés"
                                    value={e?.displayed ?? null}
                                    percent={pct(e?.displayed ?? 0, total)}
                                    icon={<Eye className="w-3.5 h-3.5" />}
                                    accent="green"
                                />
                                <KpiCard
                                    label="Cachés"
                                    value={e?.hidden ?? null}
                                    percent={pct(e?.hidden ?? 0, total)}
                                    icon={<EyeOff className="w-3.5 h-3.5" />}
                                    accent="red"
                                />
                                <KpiCard
                                    label="Fermés"
                                    value={e?.closed ?? null}
                                    percent={pct(e?.closed ?? 0, total)}
                                    icon={<XCircle className="w-3.5 h-3.5" />}
                                    accent="red"
                                />
                                <KpiCard
                                    label="Avec Instagram"
                                    value={e?.withInstagram ?? null}
                                    percent={pct(e?.withInstagram ?? 0, total)}
                                    icon={<Instagram className="w-3.5 h-3.5" />}
                                    accent="purple"
                                />
                                <KpiCard
                                    label="Business email"
                                    value={e?.withEmail ?? null}
                                    percent={pct(e?.withEmail ?? 0, total)}
                                    icon={<Mail className="w-3.5 h-3.5" />}
                                    accent="blue"
                                />
                                <KpiCard
                                    label="Avec tags"
                                    value={e?.withTags ?? null}
                                    percent={pct(e?.withTags ?? 0, total)}
                                    icon={<Eye className="w-3.5 h-3.5" />}
                                />
                                <KpiCard
                                    label="Avec sous-tags"
                                    value={e?.withSubTags ?? null}
                                    percent={pct(e?.withSubTags ?? 0, total)}
                                    icon={<Eye className="w-3.5 h-3.5" />}
                                />
                                <KpiCard
                                    label="Téléphones"
                                    value={e?.phone.total ?? null}
                                    percent={pct(phones, total)}
                                    icon={<Phone className="w-3.5 h-3.5" />}
                                    sub={[
                                        {
                                            label: "06 / 07",
                                            value: e?.phone.mobile ?? null,
                                            percent: pct(e?.phone.mobile ?? 0, phones),
                                        },
                                        {
                                            label: "Autres",
                                            value: e?.phone.other ?? null,
                                            percent: pct(e?.phone.other ?? 0, phones),
                                        },
                                    ]}
                                />
                                <KpiCard
                                    label="LinkedIn approuvés"
                                    value={p?.linkedinApproved ?? null}
                                    percent={pct(p?.linkedinApproved ?? 0, total)}
                                    icon={<Linkedin className="w-3.5 h-3.5" />}
                                    accent="blue"
                                />
                                <KpiCard
                                    label="Email perso approuvé"
                                    value={p?.personalEmailsApproved ?? null}
                                    percent={pct(p?.personalEmailsApproved ?? 0, total)}
                                    icon={<Mail className="w-3.5 h-3.5" />}
                                    accent="green"
                                    sub={[
                                        {
                                            label: "Auto",
                                            value: p?.personalEmailsAutoApproved ?? null,
                                            percent: pct(p?.personalEmailsAutoApproved ?? 0, p?.personalEmailsApproved ?? 0),
                                        },
                                        {
                                            label: "Manuel",
                                            value: p?.personalEmailsManualApproved ?? null,
                                            percent: pct(p?.personalEmailsManualApproved ?? 0, p?.personalEmailsApproved ?? 0),
                                        },
                                    ]}
                                />
                            </div>

                            <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                    <h2 className="text-base font-semibold text-gray-900">Évolution</h2>
                                    {hasActiveFilters && (
                                        <span className="text-xs text-gray-400">
                                            (snapshots globaux — filtres non applicables à l'historique)
                                        </span>
                                    )}
                                </div>
                                <KpiChart snapshots={history} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
